import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import {
  DIAGNOSTICS_REQUESTED_MESSAGE,
  DIAGNOSTICS_UNAVAILABLE_MESSAGE,
} from "@/lib/looseend/inngest-diagnostics";
import { requestInngestDiagnosticsTest } from "@/lib/looseend/inngest.functions";
import { getAccountTimeZone } from "@/lib/account/preferences.functions";

import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useSignOut } from "@/lib/auth/useSignOut";
import { useAppState } from "@/lib/looseend/store";
import { analytics } from "@/lib/analytics";
import { isDevelopmentDiagnosticsEnabled, sendSentryTestError } from "@/lib/sentry";
import { GoogleCalendarIntegrationSection } from "@/components/looseend/GoogleCalendarIntegrationSection";
import { GmailIntegrationSection } from "@/components/looseend/GmailIntegrationSection";
import { LooseEndBrandLockup } from "@/components/looseend/BrandLockup";
import { AboutPeeModal } from "@/components/looseend/AboutPeeModal";
import { ENGINE_EXPLANATION, SYSTEM_EXPLANATION } from "@/lib/looseend/pee";

export const Route = createFileRoute("/app/settings")({
  head: () => ({
    meta: [
      { title: "Settings — L∞seEnd" },
      {
        name: "description",
        content: "Your account, the time zone L∞seEnd uses for dates, and analytics choices.",
      },
      { property: "og:title", content: "Settings — L∞seEnd" },
      { property: "og:description", content: "Account, time zone and analytics settings." },
    ],
  }),
  component: SettingsPage,
});

/**
 * WP-PT — Settings shows only controls that are real under current authority:
 * account sign-in/out, the hosted time zone Real AI actually uses (read-only —
 * it is set at account creation and there is no governed update path yet), and
 * the anonymous-analytics switch. Prototype-only preferences, notification
 * controls, sample data and JSON export are deliberately absent.
 */
function SettingsPage() {
  const state = useAppState();
  const signOut = useSignOut();
  const [aboutPee, setAboutPee] = useState(false);
  const [analyticsOn, setAnalyticsOn] = useState(() => analytics.isEnabled());
  const [inngestPending, setInngestPending] = useState(false);
  const runInngestTest = useServerFn(requestInngestDiagnosticsTest);
  const readTimeZone = useServerFn(getAccountTimeZone);
  const timeZone = useQuery({
    queryKey: ["account", "time-zone", state.user?.id ?? null],
    queryFn: () => readTimeZone({}),
    staleTime: 60_000,
    retry: 1,
  });

  const timeZoneCopy = timeZone.isPending
    ? "Loading…"
    : timeZone.data?.ok
      ? timeZone.data.timeZone
      : "Unavailable right now";

  return (
    <div className="h-full overflow-y-auto px-4 py-6 sm:px-8">
      <div className="mx-auto max-w-2xl space-y-4">
        <header>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Settings</h1>
          <p className="mt-1.5 text-sm text-muted-foreground">
            {state.user?.isGuest
              ? "You're using L∞seEnd™ as a guest."
              : `Signed in as ${state.user?.email ?? "your account"}.`}
          </p>
        </header>

        <section className="rounded-2xl border border-border bg-card p-5">
          <h2 className="text-sm font-semibold text-foreground">Account</h2>
          {state.user?.isGuest ? (
            <>
              <p className="mt-1.5 text-sm text-muted-foreground">
                Your conversations are kept for this browser session. Sign in to save them and
                access them from other devices.
              </p>
              <div className="mt-4">
                <Button asChild>
                  <Link to="/auth" search={{ redirect: "/app/settings" }}>
                    Sign in to save your history
                  </Link>
                </Button>
              </div>
            </>
          ) : (
            <>
              <p className="mt-1.5 text-sm text-muted-foreground">
                Your workspace is keyed to your account id, not your email address.
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <span className="text-sm font-medium text-foreground">{state.user?.email}</span>
                <Button variant="outline" onClick={() => void signOut()}>
                  Sign out
                </Button>
              </div>
            </>
          )}
        </section>

        <section className="rounded-2xl border border-border bg-card p-5">
          <h2 className="text-sm font-semibold text-foreground">Time zone</h2>
          <p className="mt-1.5 text-sm text-muted-foreground">
            Dates like “next Friday” are resolved in this time zone when a Loop is compiled.
          </p>
          <dl className="mt-3 flex items-center justify-between gap-3 text-sm">
            <dt className="text-muted-foreground">Current</dt>
            <dd className="font-medium text-foreground" data-testid="account-time-zone">
              {timeZoneCopy}
            </dd>
          </dl>
          <p className="mt-2 text-xs text-muted-foreground">
            Set from your browser when your account was created. Changing it here isn’t available
            yet.
          </p>
        </section>

        {isDevelopmentDiagnosticsEnabled() ? <GoogleCalendarIntegrationSection /> : null}

        {isDevelopmentDiagnosticsEnabled() ? <GmailIntegrationSection /> : null}

        <section className="rounded-2xl border border-border bg-card p-5">
          <h2 className="text-sm font-semibold text-foreground">About L∞seEnd™</h2>
          <LooseEndBrandLockup variant="full" className="mt-3" />
          <div className="mt-4 space-y-2.5 text-sm leading-relaxed text-muted-foreground">
            <p>{SYSTEM_EXPLANATION}</p>
            <p>{ENGINE_EXPLANATION}</p>
          </div>
          <dl className="mt-4 grid gap-1.5 text-sm">
            <div className="flex justify-between gap-3">
              <dt className="text-muted-foreground">Release</dt>
              <dd className="text-foreground">App Alpha</dd>
            </div>
          </dl>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => setAboutPee(true)}>
              About PEE™
            </Button>
          </div>
        </section>
        <AboutPeeModal open={aboutPee} onOpenChange={setAboutPee} />

        <section className="rounded-2xl border border-border bg-card p-5">
          <h2 className="text-sm font-semibold text-foreground">Privacy</h2>
          <div className="mt-3 flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">Anonymous product analytics</p>
              <p className="text-sm text-muted-foreground">
                Counts which features are used. Never your conversations, Loop details, searches,
                names or files.
              </p>
            </div>
            <Switch
              checked={analyticsOn}
              onCheckedChange={(v) => {
                analytics.setEnabled(v);
                setAnalyticsOn(v);
                if (v) analytics.init();
              }}
              aria-label="Anonymous product analytics"
              className="mt-0.5 shrink-0"
            />
          </div>

          {/* Development-only diagnostics; never rendered on production hosts. */}
          {isDevelopmentDiagnosticsEnabled() ? (
            <div className="mt-4 border-t border-border pt-4">
              <p className="text-sm font-medium text-foreground">Developer diagnostics</p>
              <p className="text-sm text-muted-foreground">
                Only visible outside production. Sends a technical test error with no data attached.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    const sent = sendSentryTestError();
                    toast[sent ? "success" : "error"](
                      sent
                        ? "Sentry test error sent"
                        : "Sentry is not configured — no error monitoring endpoint",
                    );
                  }}
                >
                  Send Sentry test error
                </Button>
                <Button
                  variant="outline"
                  disabled={inngestPending}
                  onClick={() => {
                    setInngestPending(true);
                    void runInngestTest()
                      .then((result) => {
                        if (result?.ok) toast.success(DIAGNOSTICS_REQUESTED_MESSAGE);
                        else toast.error(DIAGNOSTICS_UNAVAILABLE_MESSAGE);
                      })
                      .catch(() => {
                        toast.error(DIAGNOSTICS_UNAVAILABLE_MESSAGE);
                      })
                      .finally(() => setInngestPending(false));
                  }}
                >
                  Run Inngest test workflow
                </Button>
              </div>
            </div>
          ) : null}
        </section>
      </div>
    </div>
  );
}
